import { z } from 'zod';
import superjson from 'superjson';
import { Selectable } from 'kysely';
import { UserAiConfigs, AiProviderTypeArrayValues } from '../../helpers/schema';

// Schema for custom models stored in JSON
const CustomModelSchema = z.object({
  name: z.string(),
  provider: z.string(),
  model: z.string(),
  endpoint: z.string().optional(),
  apiKey: z.string().optional(),
});

// Supported local LLM types
const LocalLlmTypeValues = ['ollama', 'lm_studio', 'llama_cpp', 'openai_compatible'] as const;

export const schema = z.object({
  localLlmEnabled: z.boolean().optional(),
  localLlmType: z.enum(LocalLlmTypeValues).nullable().optional(),
  localLlmEndpoint: z.string().nullable().optional(),
  localLlmApiKey: z.string().nullable().optional(),
  localLlmModels: z.array(z.string()).nullable().optional(),
  localLlmDefaultModel: z.string().nullable().optional(),
  preferredCloudProvider: z.enum(AiProviderTypeArrayValues).nullable().optional(),
  preferredCloudModel: z.string().nullable().optional(),
  preferredCloudModelApiKey: z.string().nullable().optional(),
  customModels: z.array(CustomModelSchema).nullable().optional(),
});

export type InputType = z.infer<typeof schema>;

export type OutputType = Selectable<UserAiConfigs>;

export const postUserAiConfig = async (
  body: InputType,
  init?: RequestInit,
): Promise<OutputType> => {
  const validatedInput = schema.parse(body);
  try {
    // Debug: log the outgoing payload so we can verify what the UI is sending
    // This runs in the client browser console when invoked from the frontend.
    // Keep as debug-level to avoid noisy logs in production.

    console.debug('[AI Config] Posting payload to /_api/user/ai-config', validatedInput);
  } catch (e) {
    // ignore
  }
  const result = await fetch(`/_api/user/ai-config`, {
    method: 'POST',
    credentials: 'include',
    body: superjson.stringify(validatedInput),
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!result.ok) {
    const errorObject = superjson.parse<{ error: string }>(await result.text());
    throw new Error(errorObject.error);
  }

  return superjson.parse<OutputType>(await result.text());
};
